/**
 * DemoExperience — everything a first-time demo visitor sees on top of
 * the normal product. One module that mounts once per shell:
 *
 *   • Persistent top banner ("Demo Mode — changes are not saved")
 *   • First-visit Welcome modal (per-session dismiss)
 *   • Guided tour list (opt-in from the welcome modal)
 *   • "Book a Demo" CTA (surfaces after ~75 s or 5 page views)
 *   • Anonymous engagement analytics (kind + session id, no PII)
 *   • Page-view tracking on every route change
 *
 * Everything is a no-op unless `user.is_demo === true`. Zero impact on
 * real employee sessions.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, Sparkles, ShieldCheck, Play, Calendar, Mail, Zap, ExternalLink, Info } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// ── analytics ────────────────────────────────────────────────────
function ensureSessionId() {
  const K = "rf_demo_session_id";
  let id = sessionStorage.getItem(K);
  if (!id) {
    id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)).replace(/[^a-zA-Z0-9-]/g, "");
    sessionStorage.setItem(K, id);
  }
  return id;
}

function track(kind, opts = {}) {
  try {
    api.post("/demo/analytics/event", {
      kind,
      session_id: ensureSessionId(),
      demo_role: opts.demo_role || null,
      page: opts.page || null,
      extra: opts.extra || null,
    }).catch(() => {});
  } catch { /* never let analytics break the UI */ }
}

// ── tour destinations ────────────────────────────────────────────
const TOUR_STOPS = {
  admin: [
    { to: "/admin/command",             label: "Dashboard",       hint: "Live ops overview and shift briefs" },
    { to: "/admin/loads",               label: "Loads",           hint: "Complete 11-step load lifecycle" },
    { to: "/admin/dispatch",            label: "Dispatch Board",  hint: "Assign carriers, drivers, and trucks" },
    { to: "/admin/drivers",             label: "Drivers",         hint: "Driver roster, compliance, HOS" },
    { to: "/admin/carriers",            label: "Carriers",        hint: "Onboarding, COIs, FMCSA lookups" },
    { to: "/admin/customers",           label: "Customers",       hint: "Dispatch clients — carriers we dispatch for" },
    { to: "/admin/map",                 label: "Live Map",        hint: "Real-time Google Maps tracking" },
    { to: "/admin/copilot",             label: "AI Copilot",      hint: "Natural-language ops orchestration" },
    { to: "/admin/integration-center",  label: "Integration Center", hint: "Vendors · health · traces · idempotency" },
    { to: "/admin/reports",             label: "Reports",         hint: "Financials, settlements, telemetry" },
  ],
  dispatcher: [
    { to: "/admin/command",             label: "Dashboard",       hint: "Ops overview" },
    { to: "/admin/dispatch",            label: "Dispatch Board",  hint: "Assign loads to drivers" },
    { to: "/admin/loads",               label: "Loads",           hint: "Load lifecycle" },
    { to: "/admin/map",                 label: "Live Map",        hint: "Fleet tracking" },
    { to: "/admin/copilot",             label: "AI Copilot",      hint: "Ask questions in plain English" },
  ],
  driver: [
    { to: "/driver",                    label: "Driver Home",     hint: "Today's loads" },
    { to: "/driver/hos",                label: "HOS",             hint: "Duty status and hours" },
    { to: "/driver/docs",               label: "Documents",       hint: "POD upload workflow" },
    { to: "/driver/payments",           label: "Payments",        hint: "Payout method" },
  ],
  customer: [
    { to: "/customer",                  label: "My Shipments",    hint: "Track your orders" },
  ],
};

// ── copy per role ────────────────────────────────────────────────
const ROLE_COPY = {
  admin:      "You're exploring the full Roll Fast operations suite with sample fleet data.",
  dispatcher: "You're in the dispatcher seat — assign, track, and orchestrate the fleet.",
  driver:     "You're seeing the driver PWA experience — the interface every driver uses in the cab.",
  customer:   "You're in the dispatch-client portal — the view every Roll Fast dispatch client sees for their orders.",
};

// ── main component ───────────────────────────────────────────────
export default function DemoExperience() {
  const { user } = useAuth();
  const location = useLocation();
  const isDemo = !!(user && user.is_demo);
  const role = user?.demo_role || user?.role;

  // Local UI state.
  const [showWelcome, setShowWelcome] = useState(false);
  const [showCta, setShowCta] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);
  const pageCount = useRef(0);
  const sessionStart = useRef(Date.now());

  // On mount for a demo user, fire session_start once and decide whether
  // to show the welcome modal.
  useEffect(() => {
    if (!isDemo) return;
    const dismissed = sessionStorage.getItem("rf_demo_welcome_seen");
    if (!dismissed) setShowWelcome(true);
    track("session_start", { demo_role: role, page: location.pathname });
  }, [isDemo, role, location.pathname]);

  // Track page views + drive the "book a demo" CTA timing.
  useEffect(() => {
    if (!isDemo) return;
    pageCount.current += 1;
    track("page_view", { demo_role: role, page: location.pathname });
    if (ctaDismissed) return;
    // Fire CTA once, after either 75s OR 5 unique page views — whichever
    // comes first. Feels invited rather than nagging.
    const elapsed = (Date.now() - sessionStart.current) / 1000;
    if (elapsed >= 75 || pageCount.current >= 5) {
      setShowCta(true);
    }
  }, [location.pathname, isDemo, ctaDismissed, role]);

  // Additional timer-based fallback so the CTA appears even if the visitor
  // is deep on a single page for a while.
  useEffect(() => {
    if (!isDemo || ctaDismissed) return;
    const t = setTimeout(() => setShowCta(true), 75_000);
    return () => clearTimeout(t);
  }, [isDemo, ctaDismissed]);

  // Nothing to render for non-demo users.
  if (!isDemo) return null;

  const dismissWelcome = (tookTour = false) => {
    sessionStorage.setItem("rf_demo_welcome_seen", "1");
    setShowWelcome(false);
    track(tookTour ? "tour_started" : "welcome_dismissed", { demo_role: role });
  };

  return (
    <>
      <DemoBanner />
      {showWelcome && (
        <DemoWelcomeModal
          role={role}
          onDismiss={() => dismissWelcome(false)}
          onStartTour={() => dismissWelcome(true)}
        />
      )}
      {showCta && !ctaDismissed && (
        <DemoCta
          onClose={() => { setCtaDismissed(true); }}
          role={role}
        />
      )}
    </>
  );
}

// ── Banner ───────────────────────────────────────────────────────
function DemoBanner() {
  return (
    <div
      data-testid="demo-banner"
      className="w-full flex items-center justify-center gap-2 py-1.5 px-4 text-[11px] font-medium"
      style={{
        background: "linear-gradient(90deg, rgba(229,9,20,0.14) 0%, rgba(229,9,20,0.06) 100%)",
        borderBottom: "1px solid rgba(229,9,20,0.35)",
        color: "#FCA5A5",
      }}
    >
      <Sparkles className="w-3.5 h-3.5" />
      <span>You&rsquo;re viewing a sandboxed Roll Fast demonstration. Changes are not saved.</span>
      <span className="hidden sm:inline text-neutral-400">·</span>
      <a
        href="mailto:sales@rollfastsolutions.com?subject=Roll+Fast+TMS+Demo"
        className="hidden sm:inline underline underline-offset-2 hover:text-white"
        onClick={() => track("cta_click_contact_sales", { extra: { placement: "banner" } })}
      >
        Talk to sales
      </a>
    </div>
  );
}

// ── Welcome modal ────────────────────────────────────────────────
function DemoWelcomeModal({ role, onDismiss, onStartTour }) {
  const stops = TOUR_STOPS[role] || TOUR_STOPS.admin;
  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="demo-welcome-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="w-full max-w-lg rounded-xl overflow-hidden"
        style={{
          background: "#0a0a0a",
          border: "1px solid #262626",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.02)",
        }}
      >
        <div className="relative px-6 pt-6 pb-4"
              style={{ background: "radial-gradient(circle at 30% 0%, rgba(229,9,20,0.15) 0%, transparent 60%)" }}>
          <button data-testid="demo-welcome-close"
                    onClick={onDismiss} className="absolute top-3 right-3 p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-neutral-800/60">
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ background: "rgba(229,9,20,0.18)", color: "#F87171", border: "1px solid rgba(229,9,20,0.4)" }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-90" />
            Demo Mode
          </div>
          <h2 className="mt-3 text-xl font-semibold text-white tracking-tight">
            Welcome to Roll Fast TMS
          </h2>
          <p className="mt-1.5 text-sm text-neutral-300">
            {ROLE_COPY[role] || ROLE_COPY.admin}
          </p>
        </div>
        <div className="px-6 pb-4 space-y-2.5">
          <BulletRow icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />}
                      title="All data is sample data"
                      body="Loads, carriers, drivers, and invoices are synthetic — no real customer information is ever shown." />
          <BulletRow icon={<Info className="w-4 h-4 text-sky-400" />}
                      title="Demo accounts are read-only"
                      body="Every screen is fully explorable. Actions that would modify data are safely blocked." />
          <BulletRow icon={<Zap className="w-4 h-4 text-amber-400" />}
                      title="Production integrations are simulated"
                      body="QuickBooks sync, Stripe payments, SendGrid emails, and live dispatch actions won't reach real vendors." />
        </div>
        <div className="px-6 pb-4">
          <div className="text-[11px] uppercase text-neutral-500 tracking-wider mb-2">Suggested tour</div>
          <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto pr-1">
            {stops.slice(0, 8).map((s) => (
              <Link key={s.to} to={s.to}
                      onClick={onStartTour}
                      data-testid={`tour-stop-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="group flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-neutral-300 hover:text-white hover:bg-neutral-800/60 transition">
                <span className="text-neutral-500 group-hover:text-neutral-300">→</span>
                <span className="truncate">{s.label}</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="px-6 pb-6 flex items-center gap-2">
          <button data-testid="demo-welcome-skip"
                    onClick={onDismiss}
                    className="flex-1 px-3 py-2 rounded-md text-xs font-medium text-neutral-400 hover:text-white ring-1 ring-neutral-800 hover:ring-neutral-700 transition">
            Skip — just let me explore
          </button>
          <Link to={stops[0]?.to || "/admin"}
                  data-testid="demo-welcome-start"
                  onClick={onStartTour}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-semibold text-black bg-white hover:bg-neutral-200 transition">
            <Play className="w-3.5 h-3.5" /> Start guided tour
          </Link>
        </div>
      </div>
    </div>
  );
}

function BulletRow({ icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div>
        <div className="text-sm font-medium text-neutral-100">{title}</div>
        <div className="text-xs text-neutral-400 mt-0.5">{body}</div>
      </div>
    </div>
  );
}

// ── Book-a-demo CTA ──────────────────────────────────────────────
function DemoCta({ onClose, role }) {
  const ctaRef = useRef(null);
  useEffect(() => {
    track("cta_shown", { demo_role: role });
    const t = setTimeout(() => ctaRef.current?.classList.remove("translate-y-3", "opacity-0"), 30);
    return () => clearTimeout(t);
  }, [role]);
  return (
    <div ref={ctaRef}
          data-testid="demo-cta"
          className="fixed bottom-4 right-4 z-[90] max-w-sm rounded-xl overflow-hidden opacity-0 translate-y-3 transition-all duration-300"
          style={{
            background: "#0a0a0a",
            border: "1px solid #262626",
            boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
          }}>
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">
            <Sparkles className="w-3 h-3" /> Ready for the real thing?
          </div>
          <div className="text-sm font-semibold text-white">Like what you see?</div>
          <div className="text-xs text-neutral-400 mt-0.5">
            Schedule a live walkthrough with Roll Fast Solutions.
          </div>
        </div>
        <button onClick={onClose} data-testid="demo-cta-close"
                  className="p-1 rounded-md text-neutral-500 hover:text-white hover:bg-neutral-800/60">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-4 pb-4 pt-2 grid grid-cols-1 gap-2">
        <a href="https://calendly.com/rollfastsolutions/demo"
            target="_blank" rel="noreferrer"
            onClick={() => track("cta_click_book_demo", { demo_role: role })}
            data-testid="cta-book-demo"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-semibold text-black bg-white hover:bg-neutral-200 transition">
          <Calendar className="w-3.5 h-3.5" /> Book a Demo <ExternalLink className="w-3 h-3 opacity-70" />
        </a>
        <a href="mailto:sales@rollfastsolutions.com?subject=Roll+Fast+TMS+Inquiry"
            onClick={() => track("cta_click_contact_sales", { demo_role: role })}
            data-testid="cta-contact-sales"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-neutral-200 ring-1 ring-neutral-800 hover:ring-neutral-700 hover:text-white transition">
          <Mail className="w-3.5 h-3.5" /> Contact Sales
        </a>
        <button onClick={() => { track("cta_click_free_trial", { demo_role: role }); onClose(); }}
                  disabled
                  data-testid="cta-free-trial"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-neutral-500 ring-1 ring-neutral-900 cursor-not-allowed"
                  title="Free trials launch soon">
          <Zap className="w-3.5 h-3.5" /> Start Free Trial
          <span className="text-[9px] uppercase tracking-wider text-neutral-600 ml-1">Soon</span>
        </button>
      </div>
    </div>
  );
}

// ── Feature indicator (exported for reuse in individual screens) ──
export function DemoFeatureTag({ label = "Demo", tooltip = "This action is disabled in Demo Mode." }) {
  return (
    <span
      title={tooltip}
      data-testid="demo-feature-tag"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider align-middle ml-1.5"
      style={{ background: "rgba(229,9,20,0.15)", color: "#F87171", border: "1px solid rgba(229,9,20,0.35)" }}
    >
      <span className="inline-block w-1 h-1 rounded-full bg-current opacity-90" />
      {label}
    </span>
  );
}
